import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Layout/Navbar';
import './App.css';

const LoginForm = lazy(() => import('./components/Auth/LoginForm'));
const RegisterForm = lazy(() => import('./components/Auth/RegisterForm'));
const RestaurantList = lazy(() => import('./components/Restaurant/RestaurantList'));
const RestaurantMenu = lazy(() => import('./components/Restaurant/RestaurantMenu'));
const CreateSession = lazy(() => import('./components/Session/CreateSession'));
const SessionDetail = lazy(() => import('./components/Session/SessionDetail'));
const PublicBill = lazy(() => import('./components/Session/PublicBill'));
const AdminPanel = lazy(() => import('./components/Admin/AdminPanel'));
const NotFound = lazy(() => import('./components/Layout/NotFound'));

const Loading = () => <p style={{ textAlign: 'center', marginTop: '2rem' }}>Loading...</p>;

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Loading />;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Loading />;
  return isAuthenticated ? <Navigate to="/" /> : <>{children}</>;
};

function AppRoutes() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/login" element={<PublicOnlyRoute><LoginForm /></PublicOnlyRoute>} />
        <Route path="/register" element={<PublicOnlyRoute><RegisterForm /></PublicOnlyRoute>} />
        <Route path="/bill/:sessionId/:memberId" element={<PublicBill />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <RestaurantList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurants"
          element={
            <ProtectedRoute>
              <RestaurantList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurants/:id/menu"
          element={
            <ProtectedRoute>
              <RestaurantMenu />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sessions/new"
          element={
            <ProtectedRoute>
              <CreateSession />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sessions/:id"
          element={
            <ProtectedRoute>
              <SessionDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Navbar />
        <main>
          <AppRoutes />
        </main>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
